'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { clsx } from 'clsx/lite'

import { AppUiVisual } from '../app-ui-visual'
import {
  client,
  clientMatches,
  formatHours,
  HOURS_DAY_PANEL,
  HOURS_DAY_TARGET,
  hoursOfDay,
  weekdayIndexOf,
} from '../demo-data'
import { activityLabel, useVisualCopy, type VisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, ColumnLabel, Field, fieldClasses, FieldLabel, HeaderStat } from './app-screen'
import styles from './hours-day-visual.module.css'
import { useScenario } from './use-scenario'

const DAY = HOURS_DAY_PANEL.day
const ENTRIES = hoursOfDay(DAY)

// The booking being made is the day's last entry; everything before it was already there when the
// day was opened. Read that way round, the panel cannot book something the calendar does not hold.
const BOOKING = ENTRIES[ENTRIES.length - 1]
const BOOKED_BEFORE = ENTRIES.slice(0, -1)
const BOOKING_CLIENT = client(BOOKING.nr)

if (!BOOKING.activity || !BOOKING.note || !BOOKING.at) throw new Error(`Day ${DAY} has no booking to make`)

/** Entry notes are stored as keys, so the same booking reads in the language of the page. */
function noteText(t: VisualCopy, note: string | undefined): string | undefined {
  if (note === 'entry') return t.content.entryNote
  if (note === 'concept') return t.content.conceptNote
  return note
}

/** What the lookup turns up — several near-namesakes, with the one being booked among them. */
const MATCHES = clientMatches(HOURS_DAY_PANEL.query).slice(0, 4)

const minutes = (entries: readonly { hours: string }[]) =>
  entries.reduce((sum, entry) => {
    const [h, m] = entry.hours.split(':')
    return sum + Number(h) * 60 + Number(m)
  }, 0)

/** The contract day the bar below fills towards — the same figure the calendar's footer names. */
const DAY_MINUTES = minutes([{ hours: HOURS_DAY_TARGET }])
/** Written the way the entries beside it are written, so "8:00 van 8:00" reads as one comparison. */
const DAY_TARGET = formatHours(DAY_MINUTES)

// Both totals are summed from the entries, so the header can never disagree with the list below it.
const TOTAL_BEFORE = formatHours(minutes(BOOKED_BEFORE))
const TOTAL_AFTER = formatHours(minutes(ENTRIES))

const TOTAL_BEATS = 12
const REST_BEAT = 9 // SSR / reduced-motion snapshot — the booking made, the day complete
const RESET_BEAT = 11 // final loop beat: already reads as the empty starting state

const TYPING_AT = 1
const LOOKUP_AT = 2
const KLANT_FROM = 3
const ACTIVITEIT_FROM = 4
const AANTAL_FROM = 5
const NOTE_FROM = 6
const SAVING_AT = 7
const BOOKED_FROM = 8

/** Resolves `beat` (possibly SSR/reduced-motion `null`) to the beat used to derive panel state;
 *  the final loop beat already reads as the empty start, for a seamless wrap. */
function effectiveBeat(beat: number | null): number {
  if (beat === null) return REST_BEAT
  return beat >= RESET_BEAT ? 0 : beat
}

/** The client field as it is typed: nothing, the query, then the name that was picked. */
function klantValue(beat: number): string {
  if (beat >= KLANT_FROM && beat < BOOKED_FROM) return BOOKING_CLIENT.name
  if (beat >= TYPING_AT && beat < KLANT_FROM) return HOURS_DAY_PANEL.query.slice(0, beat === TYPING_AT ? 2 : 4)
  return ''
}

/** One booked line, the way the panel writes it: client, what was done, and what was written on it. */
function EntryRow({ entry, fresh }: { entry: (typeof ENTRIES)[number]; fresh?: boolean }) {
  const { t } = useVisualCopy()
  return (
    <div className={clsx(styles.entry, fresh && styles.entryFresh)}>
      <div className="flex min-w-0 flex-col gap-[3px]">
        <span className={styles.entryName}>{client(entry.nr).name}</span>
        <span className={styles.entryMeta}>
          {activityLabel(t, entry.activity)} · {t.word.at} {entry.at}
        </span>
        <span className={styles.entryNote}>{noteText(t, entry.note)}</span>
      </div>
      <span className={clsx(styles.hours, fresh && styles.hoursFresh)}>{entry.hours}</span>
    </div>
  )
}

/**
 * Urenregistratie card 1 — the day panel: the screen that opens when you click a day in the
 * calendar. The client lookup is typed and narrows to a handful of near-namesakes, the right one is
 * picked, project, activiteit and aantal follow, and the booking lands in the day's own list with
 * the day total ticking up. Deliberately not another calendar — the month view is the page's hero;
 * this is what happens inside one of its days. Driven by `useScenario`'s 12-beat loop; `beat ===
 * null` (SSR, no-JS, `prefers-reduced-motion: reduce`) renders the resolved rest state: the booking
 * made, the day complete.
 */
export function HoursDayVisual({ className }: { className?: string }) {
  const { t, fmt } = useVisualCopy()
  const { ref, beat } = useScenario(TOTAL_BEATS, { intervalMs: 900, restBeat: REST_BEAT })
  const effBeat = effectiveBeat(beat)

  const klant = klantValue(effBeat)
  const typing = effBeat >= TYPING_AT && effBeat <= LOOKUP_AT
  const lookupOpen = effBeat === LOOKUP_AT
  const filled = effBeat >= KLANT_FROM && effBeat < BOOKED_FROM
  const activiteit = effBeat >= ACTIVITEIT_FROM && effBeat < BOOKED_FROM ? BOOKING.activity : undefined
  const aantal = effBeat >= AANTAL_FROM && effBeat < BOOKED_FROM ? BOOKING.hours : undefined
  const omschrijving = effBeat >= NOTE_FROM && effBeat < BOOKED_FROM ? noteText(t, BOOKING.note) : undefined
  const saving = effBeat === SAVING_AT
  const booked = effBeat >= BOOKED_FROM
  // Both the confirmation and the highlight on the new line are moments, not states: they hold for
  // one beat and then settle, so the panel comes to rest as a day that is simply booked.
  const justSaved = effBeat === BOOKED_FROM
  const entries = booked ? ENTRIES : BOOKED_BEFORE

  return (
    <AppUiVisual label={t.screen.hoursDay.caption} className={className}>
      <AppScreen>
        <div ref={ref} data-visual="hours-day" className={clsx(styles.root, 'flex h-full flex-col')}>
          <AppHeader
            title={t.screen.hoursDay.title}
            detail={fmt.weekdayDate(DAY, weekdayIndexOf(DAY))}
            right={<HeaderStat value={booked ? TOTAL_AFTER : TOTAL_BEFORE} label={t.word.hoursWord} />}
          />

          {/* The booking form. `relative` because the lookup drops out of the client field and over
              the fields under it, the way it does in the app. */}
          <div className={clsx(styles.form, 'relative flex flex-col gap-2 border-b px-3 py-2.5')}>
            <div className="flex items-end gap-2">
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <FieldLabel roomy>{t.word.client}</FieldLabel>
                <div
                  className={clsx(fieldClasses.box, fieldClasses.boxRoomy, filled && fieldClasses.filled, 'truncate')}
                  style={{ color: klant ? 'var(--au-text)' : 'var(--au-text2)' }}
                >
                  {klant || t.screen.hoursDay.searchClient}
                  {typing && (
                    <span aria-hidden className={styles.caret}>
                      |
                    </span>
                  )}
                </div>
              </div>
              <Field
                label={t.word.amount}
                value={aantal}
                placeholder="0:00"
                bold
                roomy
                align="right"
                className={styles.aantal}
              />
            </div>

            <div className="flex gap-2">
              <Field
                label={t.word.project}
                value={filled ? HOURS_DAY_PANEL.project : undefined}
                placeholder="—"
                roomy
                className="flex-1"
              />
              <Field label={t.word.activity} value={activiteit} placeholder="—" roomy className="flex-[2]" />
            </div>

            {/* What was written on the booking. The panel is where a booking gets its sentence, and
                it is the same sentence the entry below carries once it is saved. */}
            <div className="flex min-w-0 flex-col gap-0.5">
              <FieldLabel roomy>{t.word.description}</FieldLabel>
              <div
                className={clsx(styles.note, omschrijving && fieldClasses.filled)}
                style={{ color: omschrijving ? 'var(--au-text)' : 'var(--au-text2)' }}
              >
                {omschrijving ?? t.screen.hoursDay.whatWasWorkedOn}
              </div>
            </div>

            <div className="flex justify-end">
              <span className={clsx(styles.button, (saving || justSaved) && styles.buttonOn)}>
                {justSaved ? t.screen.hoursDay.saved : t.screen.hoursDay.saveHours}
              </span>
            </div>

            {/* Every firm whose name starts this way — which is the point: the right one is picked
                from the base, so the booking lands on the right client and not a lookalike. */}
            <div className={clsx(styles.lookup, lookupOpen && styles.lookupOpen)} aria-hidden="true">
              {MATCHES.map((match) => (
                <div
                  key={match.nr}
                  className={clsx(styles.lookupRow, match.nr === BOOKING.nr && styles.lookupRowActive)}
                >
                  <span className={styles.lookupName}>{match.name}</span>
                  <span className={styles.lookupNr}>{match.nr}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col px-3 pt-2.5 pb-3">
            <ColumnLabel right={<span className={styles.count}>{entries.length}</span>}>
              {t.screen.hoursDay.bookedThisDay}
            </ColumnLabel>
            <div className="flex flex-col">
              {entries.map((entry, index) => (
                <EntryRow
                  key={`${entry.nr}-${entry.at}`}
                  entry={entry}
                  fresh={justSaved && index === entries.length - 1}
                />
              ))}
            </div>
          </div>

          {/* What is still open on this day — the same contract hours the calendar's footer names. */}
          <div className={styles.footer}>
            <span>{t.word.dayTotal}</span>
            <span className={styles.track}>
              <span
                className={clsx(styles.fill, booked && styles.fillFull)}
                style={{ width: `${Math.min(100, Math.round((minutes(entries) / DAY_MINUTES) * 100))}%` }}
              />
            </span>
            <span>
              <b className={styles.footerValue}>{booked ? TOTAL_AFTER : TOTAL_BEFORE}</b> {t.word.of} {DAY_TARGET}
            </span>
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}
